import React, { useState, useEffect } from 'react';
import { formatClubDate } from '../../utils/clubTime';

// WMO weather code → description + emoji
function weatherDescription(code: number): { label: string; emoji: string } {
  if (code === 0) return { label: 'Clear sky', emoji: '☀️' };
  if (code <= 2) return { label: 'Partly cloudy', emoji: '⛅' };
  if (code === 3) return { label: 'Overcast', emoji: '☁️' };
  if (code <= 49) return { label: 'Foggy', emoji: '🌫️' };
  if (code <= 59) return { label: 'Drizzle', emoji: '🌦️' };
  if (code <= 69) return { label: 'Rain', emoji: '🌧️' };
  if (code <= 79) return { label: 'Snow', emoji: '❄️' };
  if (code <= 84) return { label: 'Rain showers', emoji: '🌧️' };
  if (code <= 94) return { label: 'Thunderstorm', emoji: '⛈️' };
  return { label: 'Thunderstorm', emoji: '⛈️' };
}

function windDirection(deg: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(deg / 45) % 8];
}

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function tomorrowKey(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface TideEntry {
  time: string;
  height: number;
  type: 'high' | 'low';
}

interface WeatherData {
  current: {
    temperature: number;
    weatherCode: number;
    windSpeed: number;
    windDirection: number;
    humidity: number;
    precipitation: number;
  };
  daily: Array<{
    date: string;
    weatherCode: number;
    maxTemp: number;
    minTemp: number;
    precipitation: number;
    maxWind: number;
  }>;
}

interface TideWeatherWidgetProps {
  /** Overrides the card padding/margin. Default keeps the member dashboard look. */
  className?: string;
}

const TideWeatherWidget: React.FC<TideWeatherWidgetProps> = ({ className = 'p-4 sm:p-6 mb-8' }) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [todayTides, setTodayTides] = useState<TideEntry[] | null>(null);
  const [tomorrowTides, setTomorrowTides] = useState<TideEntry[] | null>(null);
  const [tideStation, setTideStation] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Cancel on unmount (or retry): abort the requests and never set state
    // after the component has gone. Setting state after unmount threw
    // "window is not defined" in tests once the environment was torn down,
    // which failed a whole vitest run (dev build 594).
    const controller = new AbortController();
    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch weather and tides in parallel
        const [weatherRes, tidesRes] = await Promise.all([
          fetch(
            'https://api.open-meteo.com/v1/forecast' +
            '?latitude=-28.267&longitude=153.483' +
            '&current=temperature_2m,weather_code,wind_speed_10m,wind_direction_10m,relative_humidity_2m,precipitation' +
            '&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max' +
            '&timezone=Australia%2FSydney&forecast_days=4',
            { signal: controller.signal },
          ),
          fetch('/tides-2026.json', { signal: controller.signal }).catch(() => null),
        ]);
        if (cancelled) return;

        if (!weatherRes.ok) {
          throw new Error(`Weather API returned ${weatherRes.status} ${weatherRes.statusText}`);
        }
        const weatherData = await weatherRes.json();
        if (cancelled) return;

        setWeather({
          current: {
            temperature: weatherData.current.temperature_2m,
            weatherCode: weatherData.current.weather_code,
            windSpeed: weatherData.current.wind_speed_10m,
            windDirection: weatherData.current.wind_direction_10m,
            humidity: weatherData.current.relative_humidity_2m,
            precipitation: weatherData.current.precipitation,
          },
          daily: weatherData.daily.time.map((date: string, i: number) => ({
            date,
            weatherCode: weatherData.daily.weather_code[i],
            maxTemp: weatherData.daily.temperature_2m_max[i],
            minTemp: weatherData.daily.temperature_2m_min[i],
            precipitation: weatherData.daily.precipitation_sum[i],
            maxWind: weatherData.daily.wind_speed_10m_max[i],
          })),
        });

        // Load tide data if available
        if (tidesRes && tidesRes.ok) {
          const tidesData = await tidesRes.json();
          if (cancelled) return;
          setTideStation(tidesData.station || '');
          const today = todayKey();
          const tomorrow = tomorrowKey();
          if (tidesData.tides?.[today]) setTodayTides(tidesData.tides[today]);
          if (tidesData.tides?.[tomorrow]) setTomorrowTides(tidesData.tides[tomorrow]);
        }
      } catch (err) {
        if (cancelled) return;
        // Log the real error so it's visible in the browser console and
        // picked up by CloudWatch RUM's JS-error tracking — without this,
        // every failure mode (network block, CORS, rate limit, malformed
        // response) collapsed into the same unhelpful generic message,
        // making user reports of "it's not working" impossible to diagnose.
        console.error('TideWeatherWidget failed to load:', err);

        // A bare fetch() network failure (DNS block, ad-blocker, corporate
        // firewall, offline) throws a generic TypeError with no HTTP status
        // — this is the most likely cause of "works for me, not for other
        // users", since this widget calls a third-party API directly from
        // the browser with no backend proxy in between.
        if (err instanceof TypeError) {
          setError('Could not reach the weather service — this can happen if a browser extension, ad-blocker, or network firewall is blocking api.open-meteo.com. Weather still works if you try a different network or browser.');
        } else {
          setError('Could not load weather data. This is usually temporary — try refreshing in a minute.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [retryCount]);

  const formatDay = (dateStr: string, index: number) => {
    if (index === 0) return 'Today';
    if (index === 1) return 'Tomorrow';
    return formatClubDate(dateStr, { weekday: 'short' });
  };

  // Find next tide from now
  const getNextTide = (tides: TideEntry[] | null): TideEntry | null => {
    if (!tides) return null;
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    return tides.find(t => {
      const [h, m] = t.time.split(':').map(Number);
      return h * 60 + m > nowMins;
    }) || null;
  };

  const nextTide = getNextTide(todayTides) || (tomorrowTides ? tomorrowTides[0] : null);

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">🌊 Weather &amp; Tides — Tumbulgum</h2>
        <p className="text-sm text-gray-500">{formatClubDate(new Date(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        <span className="text-xs text-gray-400">Open-Meteo</span>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-fishing-blue" />
          <span className="ml-2 text-sm text-gray-500">Loading...</span>
        </div>
      )}

      {error && (
        <div className="py-4 text-center">
          <p className="text-sm text-gray-500 mb-2">{error}</p>
          <button
            onClick={() => setRetryCount((c) => c + 1)}
            className="text-sm text-fishing-blue underline hover:text-blue-700"
          >
            Try again
          </button>
        </div>
      )}

      {weather && !loading && (
        <>
          {/* Current conditions + next tide */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {/* Weather now */}
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <span className="text-5xl">{weatherDescription(weather.current.weatherCode).emoji}</span>
              <div>
                <div className="text-3xl font-bold text-gray-900">{Math.round(weather.current.temperature)}°C</div>
                <div className="text-sm text-gray-600">{weatherDescription(weather.current.weatherCode).label}</div>
                <div className="text-xs text-gray-500 mt-1">
                  💨 {Math.round(weather.current.windSpeed)} km/h {windDirection(weather.current.windDirection)}
                  &nbsp;·&nbsp;💧 {weather.current.humidity}%
                </div>
              </div>
            </div>

            {/* Tides */}
            <div className="p-4 bg-teal-50 rounded-lg border border-teal-100">
              <div className="text-xs font-semibold text-teal-700 mb-2 uppercase tracking-wide">
                🌊 Tides — {tideStation || 'Tweed River area'}
              </div>
              {todayTides ? (
                <>
                  {nextTide && (
                    <div className="mb-2">
                      <span className="text-sm font-bold text-gray-900">
                        Next: {nextTide.type === 'high' ? '▲ High' : '▼ Low'} {nextTide.time}
                      </span>
                      <span className="text-sm text-gray-600 ml-1">({nextTide.height}m)</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-1">
                    {todayTides.map((t, i) => (
                      <div key={i} className={`text-xs flex items-center gap-1 ${t.type === 'high' ? 'text-blue-700' : 'text-gray-500'}`}>
                        <span>{t.type === 'high' ? '▲' : '▼'}</span>
                        <span className="font-medium">{t.time}</span>
                        <span>{t.height}m</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-xs text-gray-500">
                  Tide data not available for today.{' '}
                  <a href="http://www.bom.gov.au/australia/tides/?port=563" target="_blank" rel="noopener noreferrer" className="text-fishing-blue underline">
                    View on BOM ↗
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Tomorrow's tides */}
          {tomorrowTides && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
              <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Tomorrow's Tides</div>
              <div className="flex flex-wrap gap-3">
                {tomorrowTides.map((t, i) => (
                  <div key={i} className={`text-xs flex items-center gap-1 ${t.type === 'high' ? 'text-blue-700' : 'text-gray-500'}`}>
                    <span>{t.type === 'high' ? '▲' : '▼'}</span>
                    <span className="font-medium">{t.time}</span>
                    <span>{t.height}m</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 4-day forecast */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {weather.daily.map((day, i) => {
              const { emoji } = weatherDescription(day.weatherCode);
              return (
                <div key={day.date} className={`rounded-lg p-3 text-center border ${i === 0 ? 'border-fishing-blue bg-blue-50' : 'border-gray-100 bg-gray-50'}`}>
                  <div className="text-xs font-semibold text-gray-500 mb-1">{formatDay(day.date, i)}</div>
                  <div className="text-2xl mb-1">{emoji}</div>
                  <div className="text-sm font-bold text-gray-900">
                    {Math.round(day.maxTemp)}° <span className="font-normal text-gray-400">{Math.round(day.minTemp)}°</span>
                  </div>
                  {day.precipitation > 0 && <div className="text-xs text-blue-600 mt-0.5">{day.precipitation}mm</div>}
                  <div className="text-xs text-gray-400 mt-0.5">{Math.round(day.maxWind)} km/h</div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-400 mt-3 text-center">
            💡 Best fishing is 2 hours either side of the tide change
            {!todayTides && (
              <> · <a href="http://www.bom.gov.au/australia/tides/?port=563" target="_blank" rel="noopener noreferrer" className="text-fishing-blue">Tweed Heads tides ↗</a></>
            )}
          </p>
        </>
      )}
    </div>
  );
};

export default TideWeatherWidget;
